import React, { useRef, useEffect, useState } from 'react';
import { Home, Menu, Search, User, Bell, Mail, ChevronRight, ChevronDown, Calendar, Clock, FileText, Activity, List, Clock3, FolderOpen, Stethoscope, FilePlus, ClipboardList, MoreHorizontal, Check } from 'lucide-react';

export interface EncounterScreensProps {
  demoValues?: {
    description: string;
    severity: string;
    date: string;
    time: string;
    note: string;
    focusedField: string | null;
    mouseClickTarget?: string | null;
  };
  onReset?: () => void;
}

const ClickRipple = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
    <div className="relative flex items-center justify-center">
      <span className="absolute w-10 h-10 rounded-full bg-blue-400/40 animate-ping" />
      <span className="w-5 h-5 rounded-full bg-blue-500/70 flex items-center justify-center">
        <svg viewBox="0 0 20 20" className="w-3 h-3 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 2l.67 8.59L9 8.5l1.5 3.5 1-.5-1.5-3.5 2.59-.41L6 2z"/>
        </svg>
      </span>
    </div>
  </div>
);

const EncounterScreens: React.FC<EncounterScreensProps> = ({ demoValues, onReset }) => {
  const [saved, setSaved] = useState(false);
  const [leftChecked, setLeftChecked] = useState(false);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const severityRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const updateBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!demoValues?.description) { setSaved(false); setLeftChecked(false); }
  }, [demoValues?.description]);

  useEffect(() => {
    if (!demoValues?.mouseClickTarget) return;
    if (demoValues.mouseClickTarget === 'left') {
      setTimeout(() => setLeftChecked(true), 400);
    } else if (demoValues.mouseClickTarget === 'update') {
      setTimeout(() => setSaved(true), 600);
    }
  }, [demoValues?.mouseClickTarget]);

  useEffect(() => {
    if (!demoValues?.focusedField) return;
    switch (demoValues.focusedField) {
      case 'description': descriptionRef.current?.focus(); break;
      case 'severity': severityRef.current?.focus(); break;
      case 'date': dateRef.current?.focus(); break;
      case 'time': timeRef.current?.focus(); break;
      case 'note': noteRef.current?.focus(); break;
    }
  }, [demoValues?.focusedField]);

  return (
    <div className="flex flex-col h-full bg-[#f5f5f5] font-sans text-[11px] text-slate-800 overflow-hidden relative">
      <div className="absolute inset-0 z-40 cursor-default" />
      {/* Purple Header */}
      <div className="bg-[#7b1fa2] text-white flex items-center justify-between px-2 py-1.5 shrink-0">
        <div className="flex items-center space-x-2">
          <Menu className="w-4 h-4 cursor-pointer" />
          <Home className="w-4 h-4 cursor-pointer" />
          <div className="bg-white text-slate-800 px-2 py-0.5 rounded flex items-center text-[10px] cursor-pointer">
            Back to: Episode Inquiry <ChevronRight className="w-3 h-3 ml-1" />
          </div>
        </div>
        
        <div className="flex-1 flex justify-center px-4">
          <div className="bg-white rounded-full flex items-center px-3 py-0.5 w-full max-w-md">
            <input 
              type="text" 
              placeholder="Search" 
              className="flex-1 bg-transparent outline-none text-slate-800 text-[11px]"
            />
            <Search className="w-3 h-3 text-slate-400" />
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Mail className="w-4 h-4 cursor-pointer" />
          <div className="relative cursor-pointer">
            <Bell className="w-4 h-4" />
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center">2</div>
          </div>
          <div className="w-6 h-6 bg-slate-300 rounded-full overflow-hidden border border-white">
            <User className="w-full h-full text-slate-500 mt-1" />
          </div>
        </div>
      </div>

      {/* Patient Banner */}
      <div className="bg-white px-4 py-2 flex justify-between border-b border-slate-200 shrink-0">
        <div className="flex space-x-8">
          <div>
            <div className="font-bold text-[12px]">MRN: 92048136</div>
            <div>Encounter No: 0000031847</div>
          </div>
          <div>
            <div>Gender: <span className="font-semibold">Male</span></div>
            <div>Full Age: <span className="font-semibold">7Y 4M 12D, DOB: 01/22/2019</span></div>
          </div>
          <div>
            <div>Weight: <span className="font-semibold">24 kg</span></div>
            <div>Blood Type: <span className="font-semibold">O Rh(D) positive</span></div>
            <div>Patient Name: <span className="font-semibold">Noah Carter</span></div>
          </div>
          <div>
            <div>Care Provider: <span className="font-semibold text-blue-600">Doctor Michael Athelas</span></div>
            <div>Location: <span className="font-semibold">Terra Bella Medical Center</span></div>
          </div>
        </div>
        <div className="flex items-center space-x-1 text-blue-600">
          <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center cursor-pointer"><User className="w-3 h-3" /></div>
          <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center cursor-pointer"><Activity className="w-3 h-3 text-green-600" /></div>
          <div className="w-5 h-5 bg-yellow-100 rounded flex items-center justify-center cursor-pointer"><FileText className="w-3 h-3 text-yellow-600" /></div>
          <div className="w-5 h-5 bg-red-100 rounded flex items-center justify-center cursor-pointer"><Activity className="w-3 h-3 text-red-600" /></div>
          <MoreHorizontal className="w-4 h-4 ml-2 text-slate-400 cursor-pointer" />
        </div>
      </div>

      {/* Allergies Banner */}
      <div className="bg-white px-4 py-1.5 border-b border-slate-200 shrink-0 text-[10px]">
        <span className="font-bold">ALLERGIES:</span> <span className="text-green-700 font-semibold">No known drug allergies</span>
      </div>

      {/* Main Content Split */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* Far Left Icon Bar */}
        <div className="w-14 bg-white border-r border-slate-200 flex flex-col items-center py-2 space-y-4 shrink-0 overflow-y-auto">
          <div className="flex flex-col items-center cursor-pointer text-slate-500 hover:text-slate-800">
            <FilePlus className="w-5 h-5 mb-1" />
            <span className="text-[9px]">New</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer text-teal-600 bg-teal-50 w-full py-2 border-l-2 border-teal-600">
            <Activity className="w-5 h-5 mb-1" />
            <span className="text-[9px]">Current</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer text-slate-500 hover:text-slate-800">
            <ClipboardList className="w-5 h-5 mb-1" />
            <span className="text-[9px]">Mini-EPR</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer text-slate-500 hover:text-slate-800">
            <List className="w-5 h-5 mb-1" />
            <span className="text-[9px]">To-Do</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer text-slate-500 hover:text-slate-800">
            <Clock3 className="w-5 h-5 mb-1" />
            <span className="text-[9px]">History</span>
          </div>
        </div>

        {/* Actions Panel Menu */}
        <div className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0">
          <div className="bg-slate-100 px-3 py-2 font-bold border-b border-slate-200 flex justify-between items-center">
            Actions Panel
            <ChevronRight className="w-3 h-3 text-slate-400" />
          </div>
          <div className="p-2 border-b border-slate-200">
            <div className="flex items-center border border-slate-300 rounded px-2 py-1 bg-white">
              <input type="text" placeholder="Quick Actions" className="flex-1 outline-none text-[11px]" />
              <Search className="w-3 h-3 text-slate-400" />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {/* Reason for Encounter */}
            <div className="bg-slate-50 px-2 py-1.5 border-b border-slate-200 flex items-center cursor-pointer font-semibold">
              <ChevronDown className="w-3 h-3 mr-1" /> Reason for Encounter
            </div>
            <div className="bg-[#fff9c4] px-6 py-1.5 border-b border-slate-200 cursor-pointer text-blue-700">
              Chief Complaint / Reason for Encounter
            </div>

            {/* History */}
            <div className="bg-slate-50 px-2 py-1.5 border-b border-slate-200 flex items-center cursor-pointer font-semibold">
              <ChevronDown className="w-3 h-3 mr-1" /> History
            </div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Alert</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Allergy</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Medical History</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Surgical History</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Family History</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Medication History</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Medication History Entry</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Social History</div>
            <div className="px-6 py-1.5 border-b border-slate-200 cursor-pointer text-slate-700 hover:bg-slate-50">Occupational History</div>

            {/* Assessments */}
            <div className="bg-slate-50 px-2 py-1.5 border-b border-slate-200 flex items-center cursor-pointer font-semibold">
              <ChevronDown className="w-3 h-3 mr-1" /> Assessments
            </div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Medical Pictures</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Vital Signs</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Physical Exams</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add Diagnosis</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Copy Diagnosis</div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Venous Thromboembolism (VTE) Risk...</div>
            <div className="px-6 py-1.5 border-b border-slate-200 cursor-pointer text-blue-600 hover:bg-slate-50">Add Review of System</div>

            {/* Plan and Actions */}
            <div className="bg-slate-50 px-2 py-1.5 border-b border-slate-200 flex items-center cursor-pointer font-semibold">
              <ChevronDown className="w-3 h-3 mr-1" /> Plan and Actions
            </div>
            <div className="px-6 py-1.5 border-b border-slate-100 cursor-pointer text-blue-600 hover:bg-slate-50">Add an Order</div>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 flex flex-col bg-white m-2 border border-slate-300 shadow-sm overflow-hidden">
          <div className="bg-slate-100 px-3 py-2 font-bold border-b border-slate-300">
            Reason for Encounter
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="bg-slate-100 px-2 py-1.5 font-semibold border border-slate-300 flex items-center mb-4">
              <ChevronDown className="w-3 h-3 mr-1" /> Reason for Encounter
            </div>

            <div className="grid grid-cols-[180px_1fr] gap-y-3 gap-x-4 max-w-3xl ml-4">
              <div className={`text-right ${demoValues?.description ? 'text-slate-800' : 'text-red-600'}`}>*Description (Snomed) translated</div>
              <div className="flex items-center">
                <input 
                  ref={descriptionRef}
                  value={demoValues?.description ?? ''}
                  readOnly
                  type="text" 
                  className="border border-slate-300 px-2 py-1 w-full max-w-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" 
                />
                <Search className="w-4 h-4 ml-2 text-slate-400 cursor-pointer" />
              </div>

              <div className="text-right">Severity (Snomed) translated</div>
              <div className="flex items-center">
                <input 
                  ref={severityRef}
                  value={demoValues?.severity ?? ''}
                  readOnly
                  type="text" 
                  className="border border-slate-300 px-2 py-1 w-full max-w-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" 
                />
                <Search className="w-4 h-4 ml-2 text-slate-400 cursor-pointer" />
              </div>

<div className={`text-right ${demoValues?.date ? 'text-slate-800' : 'text-red-600'}`}>*Onset Date</div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-slate-300 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                  <input 
                    ref={dateRef}
                    value={demoValues?.date ?? ''}
                    readOnly
                    type="text" 
                    className="px-2 py-1 w-24 outline-none" 
                  />
                  <Calendar className="w-4 h-4 mx-1 text-teal-600" />
                </div>
                <div className="flex items-center">
                  <span className="mr-2">Onset Time</span>
                  <div className="flex items-center border border-slate-300 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                    <input 
                      ref={timeRef}
                      value={demoValues?.time ?? ''}
                      readOnly
                      type="text" 
                      className="px-2 py-1 w-20 outline-none" 
                    />
                    <Clock className="w-4 h-4 mx-1 text-teal-600" />
                  </div>
                </div>
              </div>

              <div></div>
              <div className="flex items-center space-x-6 mt-1">
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input type="checkbox" className="w-3 h-3" />
                  <span>Create A Problem</span>
                </label>
              </div>

              <div></div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input type="checkbox" className="w-3 h-3" />
                  <span>Right</span>
                </label>
                <label className="relative flex items-center space-x-1 cursor-pointer">
                  {demoValues?.mouseClickTarget === 'left' && <ClickRipple />}
                  <input type="checkbox" className="w-3 h-3" checked={leftChecked} onChange={() => setLeftChecked(v => !v)} />
                  <span>Left</span>
                </label>
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input type="checkbox" className="w-3 h-3" />
                  <span>Both</span>
                </label>
              </div>

              <div className="text-right mt-4">Dictation Note</div>
              <div className="mt-4">
                <textarea
                  id="dictation-note"
                  ref={noteRef}
                  value={demoValues?.note ?? ''}
                  readOnly
                  className="w-full h-64 border border-slate-300 p-2 outline-none resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-[11px]"
                />
              </div>
            </div>

          </div>

          <div className="bg-slate-100 px-2 py-1.5 font-semibold border border-slate-300 flex items-center shrink-0">
            <ChevronRight className="w-3 h-3 mr-1" /> Details
          </div>

          {/* Footer Buttons */}
          <div className="bg-slate-100 px-4 py-2 border-t border-slate-300 flex justify-end space-x-2 shrink-0">
            <button
              onClick={() => {
                setSaved(false);
                if (onReset) onReset();
              }}
              className="px-4 py-1.5 bg-white border border-slate-300 rounded text-slate-700 hover:bg-slate-50"
            >
              Reset
            </button>
            <button
              ref={updateBtnRef}
              onClick={() => setSaved(true)}
              className={`relative px-4 py-1.5 text-white rounded transition-all flex items-center space-x-1.5 ${saved ? 'bg-green-600' : 'bg-slate-500 hover:bg-slate-600'}`}
            >
              {demoValues?.mouseClickTarget === 'update' && <ClickRipple />}
              {saved ? (
                <><Check className="w-3.5 h-3.5" /><span>Saved</span></>
              ) : (
                <span>Update</span>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EncounterScreens;
